import { Button } from '@racefront/shadcn-ui'
import type { AccountRole } from '@racefront/types'
import { ExternalLink } from 'lucide-react'
import { FormattedMessage } from 'react-intl'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../../data/stores/auth.store'

const ADMIN_ROLES: AccountRole[] = ['organizer', 'admin']
const PARTICIPANT_ROLE: AccountRole = 'profile_user'

const hasId = (value?: number) => Number(value) > 0

export function GoToAppLinks() {
  const { pathname } = useLocation()
  const [params] = useSearchParams()
  const { session, user } = useAuthStore()

  const isEmbedded = 'ReactNativeWebView' in window || pathname.startsWith('/embedded')
  const isCompletionGate = !!params.get('redirectAfterUpdate') && user?.profileComplete === false

  if (!user || isEmbedded || isCompletionGate) return null

  const idToken = session?.idTokenDecoded
  const roles = [user.role ?? []].flat()
  const groups = idToken?.['cognito:groups'] ?? []
  const hasRole = (role: AccountRole) => roles.includes(role) || groups.includes(role)

  const hasAdminAccess = ADMIN_ROLES.some(hasRole) || hasId(idToken?.['custom:organizer_id'])
  const hasParticipantAccess = hasRole(PARTICIPANT_ROLE) || hasId(idToken?.['custom:user_id'])

  const adminUrl = import.meta.env.VITE_ADMIN_URL
  const webUrl = import.meta.env.VITE_WEB_URL

  const showAdminLink = hasAdminAccess && !!adminUrl
  const showWebLink = (hasParticipantAccess || !hasAdminAccess) && !!webUrl

  if (!showAdminLink && !showWebLink) return null

  return (
    <div className="flex flex-wrap items-center gap-2">
      {showAdminLink && (
        <Button asChild size="sm">
          <a href={adminUrl} target="_blank" rel="noopener noreferrer">
            <FormattedMessage id="account.go_to_admin" defaultMessage="Go to Admin" />
            <ExternalLink className="ml-2 h-4 w-4" />
          </a>
        </Button>
      )}

      {showWebLink && (
        <Button
          asChild
          size="sm"
          variant={showAdminLink ? 'outline' : 'default'}
          className={
            showAdminLink
              ? 'border-primary/50 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary'
              : undefined
          }
        >
          <a href={webUrl} target="_blank" rel="noopener noreferrer">
            <FormattedMessage id="account.go_to_raceid" defaultMessage="Go to RaceID" />
            <ExternalLink className="ml-2 h-4 w-4" />
          </a>
        </Button>
      )}
    </div>
  )
}
